import { useEffect, useMemo, useState } from 'react'
import { Modal, Input } from 'antd'
import { ShopOutlined, CheckOutlined } from '@ant-design/icons'
import { useStore } from '@/app/StoreProvider'
import { compareNaturalText } from '@/shared/formatters'
import styles from './CommandPalette.module.css'

interface Props {
  open: boolean
  onClose: () => void
}

export function StorePicker({ open, onClose }: Props) {
  const { stores, storeId, setStoreId, recentIds, loading } = useStore()
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (open) setQuery('')
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = [...stores]
    list.sort((a, b) => {
      const aRecent = recentIds.includes(String(a.id))
      const bRecent = recentIds.includes(String(b.id))
      if (aRecent !== bRecent) return aRecent ? -1 : 1

      const byName = compareNaturalText(a.name || a.id, b.name || b.id)
      if (byName !== 0) return byName
      return compareNaturalText(a.id, b.id)
    })
    if (!q) return list
    return list.filter((s) => {
      const name = (s.name || '').toLowerCase()
      const id = String(s.id)
      return name.includes(q) || id.includes(q)
    })
  }, [stores, query, recentIds])

  const firstNonRecentIndex = filtered.findIndex(
    (store) => !recentIds.includes(String(store.id)),
  )

  const pick = (id: string) => {
    setStoreId(id)
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={480}
      centered
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      className={styles.modal}
    >
      <div className={styles.root} role="dialog" aria-label="Выбор магазина">
        <Input
          autoFocus
          size="large"
          placeholder="Найти магазин…"
          prefix={<ShopOutlined style={{ color: 'var(--text-tertiary)' }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && filtered[0]) pick(String(filtered[0].id))
          }}
          className={styles.input}
          variant="borderless"
        />
        <ul className={styles.list} role="listbox">
          {loading ? (
            <li className={styles.empty}>Загрузка…</li>
          ) : filtered.length === 0 ? (
            <li className={styles.empty}>Ничего не найдено</li>
          ) : (
            filtered.map((s, index) => {
              const id = String(s.id)
              const active = id === storeId
              const recent = recentIds.includes(id)
              return (
                <li
                  key={id}
                  className={
                    firstNonRecentIndex > 0 && index === firstNonRecentIndex
                      ? styles.afterRecent
                      : undefined
                  }
                >
                  <button
                    type="button"
                    className={`${styles.item} ${active ? styles.itemActive : ''}`}
                    onClick={() => pick(id)}
                    role="option"
                    aria-selected={active}
                  >
                    <span className={styles.itemMain}>
                      <span>{s.name || `Магазин ${id}`}</span>
                      {recent && !active ? <span className={styles.badge}>недавний</span> : null}
                    </span>
                    <span className={styles.path}>
                      {active ? <CheckOutlined /> : `ID ${id}`}
                    </span>
                  </button>
                </li>
              )
            })
          )}
        </ul>
        <div className={styles.hint}>
          <kbd>Enter</kbd> выбрать · <kbd>Esc</kbd> закрыть
        </div>
      </div>
    </Modal>
  )
}
